import { motion } from "framer-motion";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  roomTypes,
  currentFlooringOptions,
  SpaceDetailsData,
} from "@/lib/validation";

interface SpaceDetailsStepProps {
  data: SpaceDetailsData;
  onDataChange: (data: SpaceDetailsData) => void;
  errors?: Record<string, string[]>;
}

const roomIcons: Record<string, string> = {
  "Living Room": "🛋️",
  Bedroom: "🛏️",
  Kitchen: "🍳",
  Bathroom: "🚿",
  "Dining Room": "🍽️",
  Hallway: "🚪",
  Basement: "🏠",
  Other: "📍",
};

const SpaceDetailsStep = ({
  data,
  onDataChange,
  errors,
}: SpaceDetailsStepProps) => {
  const toggleRoom = (room: (typeof roomTypes)[number]) => {
    const newRooms = data.rooms.includes(room)
      ? data.rooms.filter((r) => r !== room)
      : [...data.rooms, room];
    onDataChange({ ...data, rooms: newRooms as typeof data.rooms });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div className="text-center mb-8">
        <h2 className="font-serif text-2xl md:text-3xl font-bold text-foreground mb-2">
          Tell us about your space
        </h2>
        <p className="text-muted-foreground">
          This helps us provide an accurate estimate for your project.
        </p>
      </div>

      {/* Approximate Size */}
      <div className="space-y-2">
        <Label htmlFor="size" className="text-base font-medium">
          Approximate size (sq ft)
        </Label>
        <Input
          id="size"
          type="number"
          placeholder="e.g., 500"
          value={data.approximateSize}
          onChange={(e) =>
            onDataChange({ ...data, approximateSize: e.target.value })
          }
          className="h-12 text-base"
        />
        {errors?.approximateSize && (
          <p className="text-destructive text-sm">{errors.approximateSize[0]}</p>
        )}
      </div>

      {/* Rooms */}
      <div className="space-y-3">
        <Label className="text-base font-medium">
          Which rooms need new flooring?
        </Label>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {roomTypes.map((room) => {
            const isSelected = data.rooms.includes(room);
            return (
              <motion.div
                key={room}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <label
                  className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleRoom(room)}
                    className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  />
                  <span className="text-lg">{roomIcons[room]}</span>
                  <span className="text-sm text-foreground">{room}</span>
                </label>
              </motion.div>
            );
          })}
        </div>
        {errors?.rooms && (
          <p className="text-destructive text-sm">{errors.rooms[0]}</p>
        )}
      </div>

      {/* Current Flooring */}
      <div className="space-y-2">
        <Label htmlFor="current" className="text-base font-medium">
          What's your current flooring?
        </Label>
        <Select
          value={data.currentFlooring}
          onValueChange={(value) =>
            onDataChange({
              ...data,
              currentFlooring: value as typeof data.currentFlooring,
            })
          }
        >
          <SelectTrigger className="h-12 text-base">
            <SelectValue placeholder="Select your current flooring" />
          </SelectTrigger>
          <SelectContent>
            {currentFlooringOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors?.currentFlooring && (
          <p className="text-destructive text-sm">{errors.currentFlooring[0]}</p>
        )}
      </div>
    </motion.div>
  );
};

export default SpaceDetailsStep;
